import React from 'react';
import AdminNavbar from "../../Navbars/AdminNavbar.js";
import AdminSidebar from '../../Sidebar/AdminSidebar';
import { ApiGatewayApis } from '../../../apis/ApiGatewayApis';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import { Oval } from 'react-loader-spinner';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Activity,
    Users,
    Key,
    Package,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Clock,
    Zap,
    Server,
    Database,
    RefreshCw,
    ArrowRight,
    TrendingUp,
    Shield,
    UserPlus,
    FileText,
} from 'lucide-react';

function ApiGatewayDashboard() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [overview, setOverview] = React.useState<any>(null);
    const [health, setHealth] = React.useState<any>(null);
    const [period, setPeriod] = React.useState<number>(30);

    React.useEffect(() => {
        loadDashboard();
    }, [period]);

    const loadDashboard = () => {
        setLoading(true);

        Promise.all([
            ApiGatewayApis.getAnalyticsOverview(period),
            ApiGatewayApis.getSystemHealth(),
        ]).then(([overviewRes, healthRes]: any[]) => {
            setLoading(false);
            if (overviewRes?.data?.data) setOverview(overviewRes.data.data);
            if (healthRes?.data?.data) setHealth(healthRes.data.data);
        }).catch((error: any) => {
            setLoading(false);
            console.error('Dashboard load error:', error);
            toast.error('Failed to load dashboard data. Check your admin token.');
        });
    };

    const getHealthColor = (status: string) => {
        switch (status) {
            case 'ok': return 'text-green-600 bg-green-50';
            case 'degraded': return 'text-yellow-600 bg-yellow-50';
            case 'down': return 'text-red-600 bg-red-50';
            default: return 'text-gray-600 bg-gray-50';
        }
    };

    const getHealthIcon = (status: string) => {
        switch (status) {
            case 'ok': return <CheckCircle size={16} className="text-green-600" />;
            case 'degraded': return <AlertTriangle size={16} className="text-yellow-600" />;
            case 'down': return <XCircle size={16} className="text-red-600" />;
            default: return <Clock size={16} className="text-gray-400" />;
        }
    };

    return (
        <>
            <AdminSidebar title="API Gateway" />
            <div className="relative md:ml-64 bg-[#FAFAFA]">
                <AdminNavbar title="API Gateway" />

                <div className='lg:px-10 pb-6 px-6 pt-5 bg-[#FAFAFA] min-h-screen'>

                    {/* Header */}
                    <div className='flex justify-between items-center mb-6'>
                        <div>
                            <h1 className='text-[20px] font-bold text-[#263238]'>API Gateway Dashboard</h1>
                            <p className='text-[13px] text-[#808080] mt-1'>Monitor partners, API keys, usage, and system health</p>
                        </div>
                        <div className='flex items-center gap-3'>
                            <select
                                value={period}
                                onChange={(e) => setPeriod(parseInt(e.target.value))}
                                className='bg-white border border-[#DADADA] text-sm rounded-[5px] px-3 py-2'
                            >
                                <option value={7}>Last 7 days</option>
                                <option value={30}>Last 30 days</option>
                                <option value={90}>Last 90 days</option>
                            </select>
                            <button
                                onClick={loadDashboard}
                                className='bg-white border border-[#DADADA] p-2 rounded-[5px] hover:bg-gray-50'
                                title='Refresh'
                            >
                                <RefreshCw size={18} className={loading ? 'animate-spin text-[#E04343]' : 'text-gray-600'} />
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className='flex justify-center py-20'>
                            <Oval visible={true} height="40" width="40" color="#E04343" secondaryColor="#FCBF94" ariaLabel="loading" />
                        </div>
                    ) : (
                        <>
                            {/* Stats Cards Row */}
                            <div className='grid lg:grid-cols-4 md:grid-cols-2 grid-cols-1 gap-4 mb-6'>
                                <div className='bg-white shadow-md rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[3px] border-[#E04343]'>
                                    <div className='flex justify-between items-start'>
                                        <div>
                                            <h5 className='text-[12px] text-[#808080]'>API Requests</h5>
                                            <h1 className='text-[24px] font-bold mt-1'>
                                                {overview?.api_requests?.total?.toLocaleString() || 0}
                                            </h1>
                                        </div>
                                        <div className='bg-red-50 p-2 rounded-lg'>
                                            <Activity size={20} className='text-[#E04343]' />
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-2 mt-2'>
                                        <span className='text-[11px] text-red-500'>
                                            {overview?.api_requests?.errors || 0} errors
                                        </span>
                                        <span className='text-[11px] text-[#808080]'>
                                            · avg {overview?.api_requests?.avg_response_time || 0}ms
                                        </span>
                                    </div>
                                </div>

                                <div className='bg-white shadow-md rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[3px] border-[#3B82F6]'>
                                    <div className='flex justify-between items-start'>
                                        <div>
                                            <h5 className='text-[12px] text-[#808080]'>Active Partners</h5>
                                            <h1 className='text-[24px] font-bold mt-1'>
                                                {overview?.partners?.active || 0}
                                            </h1>
                                        </div>
                                        <div className='bg-blue-50 p-2 rounded-lg'>
                                            <Users size={20} className='text-[#3B82F6]' />
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-2 mt-2'>
                                        <span className='text-[11px] text-yellow-600'>
                                            {overview?.partners?.pending || 0} pending
                                        </span>
                                        <span className='text-[11px] text-[#808080]'>
                                            · {overview?.partners?.total || 0} total
                                        </span>
                                    </div>
                                </div>

                                <div className='bg-white shadow-md rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[3px] border-[#10B981]'>
                                    <div className='flex justify-between items-start'>
                                        <div>
                                            <h5 className='text-[12px] text-[#808080]'>Active API Keys</h5>
                                            <h1 className='text-[24px] font-bold mt-1'>
                                                {overview?.api_keys?.active || 0}
                                            </h1>
                                        </div>
                                        <div className='bg-green-50 p-2 rounded-lg'>
                                            <Key size={20} className='text-[#10B981]' />
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-2 mt-2'>
                                        <span className='text-[11px] text-[#808080]'>
                                            {overview?.api_keys?.total || 0} total
                                        </span>
                                    </div>
                                </div>

                                <div className='bg-white shadow-md rounded-[10px] px-[18px] pt-[18px] pb-[14px] border-t-[3px] border-[#8B5CF6]'>
                                    <div className='flex justify-between items-start'>
                                        <div>
                                            <h5 className='text-[12px] text-[#808080]'>API Shipments</h5>
                                            <h1 className='text-[24px] font-bold mt-1'>
                                                {overview?.shipments?.from_api || 0}
                                            </h1>
                                        </div>
                                        <div className='bg-purple-50 p-2 rounded-lg'>
                                            <Package size={20} className='text-[#8B5CF6]' />
                                        </div>
                                    </div>
                                    <div className='flex items-center gap-2 mt-2'>
                                        <span className='text-[11px] text-[#808080]'>
                                            of {overview?.shipments?.total || 0} total
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {/* System Health + Quick Actions Row */}
                            <div className='grid lg:grid-cols-3 gap-4 mb-6'>
                                {/* System Health Panel */}
                                <div className='lg:col-span-2 bg-white shadow-md rounded-[10px] p-5'>
                                    <div className='flex justify-between items-center mb-4'>
                                        <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2'>
                                            <Server size={18} />
                                            System Health
                                        </h3>
                                        {health?.status && (
                                            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${getHealthColor(health.status)}`}>
                                                {health.status.toUpperCase()}
                                            </span>
                                        )}
                                    </div>

                                    {health?.components ? (
                                        <div className='grid md:grid-cols-2 gap-3'>
                                            {Object.entries(health.components).map(([key, comp]: [string, any]) => (
                                                <div key={key} className='flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3'>
                                                    <div className='flex items-center gap-2'>
                                                        {getHealthIcon(comp?.status || 'unknown')}
                                                        <span className='text-[13px] font-medium capitalize'>{key.replace(/_/g, ' ')}</span>
                                                    </div>
                                                    <span className={`text-[12px] font-semibold ${comp?.status === 'ok' ? 'text-green-600' :
                                                        comp?.status === 'degraded' ? 'text-yellow-600' : 'text-red-600'
                                                        }`}>
                                                        {comp?.status || 'N/A'}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className='text-[13px] text-[#808080]'>Unable to fetch health status</p>
                                    )}
                                </div>

                                {/* Quick Actions Panel */}
                                <div className='bg-white shadow-md rounded-[10px] p-5'>
                                    <h3 className='text-[15px] font-semibold text-[#263238] flex items-center gap-2 mb-4'>
                                        <Zap size={18} />
                                        Quick Actions
                                    </h3>
                                    <div className='space-y-2'>
                                        <button
                                            onClick={() => navigate('/admin/api-gateway/partners')}
                                            className='w-full flex items-center justify-between bg-gray-50 hover:bg-red-50 rounded-lg px-4 py-3 transition text-left'
                                        >
                                            <span className='flex items-center gap-2 text-[13px]'>
                                                <Users size={16} className='text-[#E04343]' />
                                                Manage Partners
                                            </span>
                                            <ArrowRight size={14} className='text-gray-400' />
                                        </button>

                                        <button
                                            onClick={() => navigate('/admin/api-gateway/invoices')}
                                            className='w-full flex items-center justify-between bg-gray-50 hover:bg-red-50 rounded-lg px-4 py-3 transition text-left'
                                        >
                                            <span className='flex items-center gap-2 text-[13px]'>
                                                <FileText size={16} className='text-[#E04343]' />
                                                Partner Invoices
                                            </span>
                                            <ArrowRight size={14} className='text-gray-400' />
                                        </button>




                                        <button
                                            onClick={() => navigate('/admin/api-gateway/api-keys')}
                                            className='w-full flex items-center justify-between bg-gray-50 hover:bg-red-50 rounded-lg px-4 py-3 transition text-left'
                                        >
                                            <span className='flex items-center gap-2 text-[13px]'>
                                                <Key size={16} className='text-[#E04343]' />
                                                Manage API Keys
                                            </span>
                                            <ArrowRight size={14} className='text-gray-400' />
                                        </button>

                                        <button
                                            onClick={() => navigate('/admin/api-gateway/analytics')}
                                            className='w-full flex items-center justify-between bg-gray-50 hover:bg-red-50 rounded-lg px-4 py-3 transition text-left'
                                        >
                                            <span className='flex items-center gap-2 text-[13px]'>
                                                <TrendingUp size={16} className='text-[#E04343]' />
                                                View Analytics
                                            </span>
                                            <ArrowRight size={14} className='text-gray-400' />
                                        </button>

                                        <button
                                            onClick={() => navigate('/admin/api-gateway/system')}
                                            className='w-full flex items-center justify-between bg-gray-50 hover:bg-red-50 rounded-lg px-4 py-3 transition text-left'
                                        >
                                            <span className='flex items-center gap-2 text-[13px]'>
                                                <Shield size={16} className='text-[#E04343]' />
                                                System & Cron Tasks
                                            </span>
                                            <ArrowRight size={14} className='text-gray-400' />
                                        </button>


                                        <button
                                            onClick={() => navigate('/admin/api-gateway/portal-accounts')}
                                            className='w-full flex items-center justify-between bg-gray-50 hover:bg-red-50 rounded-lg px-4 py-3 transition text-left'
                                        >
                                            <span className='flex items-center gap-2 text-[13px]'>
                                                <UserPlus size={16} className='text-[#E04343]' />
                                                Portal Accounts
                                            </span>
                                            <ArrowRight size={14} className='text-gray-400' />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
}

export default ApiGatewayDashboard;